import { Component } from './live-codec.js';

export const UnknownStyle = 0xffffffff;
export const EquipmentKind = Object.freeze({ None: 0, Weapon: 1, Wearable: 2 });

/** Source bodyparts encode mixed-radix submodel selections, not bit masks. */
export function setBodygroup(body, groups, index, state) {
    const group = groups[index];
    if (!group || !Number.isSafeInteger(group.base) || group.base <= 0 || !Number.isInteger(group.count)
        || !Number.isInteger(state) || state < 0 || state >= group.count) return body;
    const value = body + (state - Math.floor(body / group.base) % group.count) * group.base;
    return Number.isSafeInteger(value) && value >= 0 && value <= 0xffffffff ? value : body;
}

export function resolveBodygroups(schema, groups, { body = 0, equipment = [], managed = [], self } = {}) {
    const named = new Map(groups.map((group, index) => [group.name.toLowerCase(), index]));
    const indexOf = name => named.get(String(name).toLowerCase());
    if (self) {
        const rule = schema.bodygroupRules(self.item, self.team, self.style);
        const group = rule.itemGroup;
        return { body: group ? setBodygroup(body, groups, indexOf(group.name), Number(group.submodel_index)) : body, managed: [] };
    }
    const rules = equipment.map(item => ({ ...item, rule: schema.bodygroupRules(item.item, item.team, item.style) }));
    const controlled = new Set(managed.filter(index => Number.isInteger(index) && index >= 0 && index < groups.length));
    for (const name of schema.managedBodygroupNames()) { const index = indexOf(name); if (index !== undefined) controlled.add(index); }
    for (const { rule } of rules) if (Number.isInteger(rule.worldGroup) && rule.worldGroup >= 0 && rule.worldGroup < groups.length
        && Number.isInteger(rule.worldState) && rule.worldState >= 0 && rule.worldState < groups[rule.worldGroup].count) controlled.add(rule.worldGroup);
    for (const index of controlled) body = setBodygroup(body, groups, index, 0);
    const phase = item => item.kind === EquipmentKind.Wearable ? 1 : item.rule.deployedOnly ? 2 : 0;
    rules.sort((a, b) => phase(a) - phase(b) || a.index - b.index);
    for (const item of rules) {
        const { rule } = item;
        if (item.kind === EquipmentKind.Weapon && rule.deployedOnly && !item.active) continue;
        for (const [name, state] of Object.entries(rule.player)) if (Number(state) === 1) body = setBodygroup(body, groups, indexOf(name), 1);
        for (const name of rule.hidden) body = setBodygroup(body, groups, indexOf(name), 1);
        body = setBodygroup(body, groups, rule.worldGroup, rule.worldState);
    }
    return { body, managed: [...controlled].sort((a, b) => a - b) };
}

export function equipmentKind(entity) {
    const effects = entity.state.components[Component.Effects], appearance = entity.state.components[Component.Appearance];
    if (!effects || appearance?.category === 4 || effects.nativeClass === 'CTFWearableVM') return 0;
    if (effects.equipmentKind !== undefined) return effects.equipmentKind;
    // Old publishers lack native kind/style. Preserve standard rules for their
    // recognized TF2 equipment while avoiding projectile launcher item metadata.
    if (appearance?.category !== 2) return 0;
    if (/^(CTFWearable|CEconWearable|CTFPowerupBottle)/.test(effects.nativeClass ?? '')) return EquipmentKind.Wearable;
    return EquipmentKind.Weapon;
}

/** Relationships are evaluated after the entire batch, never in row order. */
export function equipmentByOwner(entities) {
    const owners = new Map();
    for (const entity of entities.values()) {
        const kind = equipmentKind(entity), effects = entity.state.components[Component.Effects];
        if (!kind || kind === EquipmentKind.Weapon && effects.flags & 128) continue;
        const ownerKey = `${effects.ownerIndex}:${effects.ownerGeneration}`, owner = entities.get(ownerKey);
        const player = owner?.state.components[Component.Player];
        if (!player) continue;
        const list = owners.get(ownerKey) ?? []; owners.set(ownerKey, list);
        list.push({ item: effects.item, team: effects.team, style: effects.itemStyle ?? UnknownStyle, kind, index: entity.state.index,
            active: player.weaponIndex === entity.state.index && player.weaponGeneration === entity.state.generation });
    }
    for (const list of owners.values()) list.sort((a, b) => a.index - b.index);
    return owners;
}

export class LiveBodygroups {
    constructor(rpc, changed, diagnostic) { this.rpc = rpc; this.changed = changed; this.diagnostic = diagnostic; this.revision = 0; }
    clear() { this.revision++; }
    reconcile(entities, dictionary) {
        const owners = equipmentByOwner(entities), revision = this.revision;
        for (const entity of entities.values()) {
            const appearance = entity.state.components[Component.Appearance], player = entity.state.components[Component.Player];
            const kind = equipmentKind(entity), effects = entity.state.components[Component.Effects];
            const path = dictionary.get(`1:${appearance?.model}`);
            // Unknown styles cannot modify an equipment model. Its streamed
            // body already carries per-class/code-controlled selections; avoid
            // fetching every ordinary weapon/hat MDL just to return that body.
            const styledEquipment = kind && effects.itemStyle !== undefined && effects.itemStyle !== UnknownStyle;
            if (!path?.endsWith('.mdl') || !player && !styledEquipment || entity.state.components[Component.Ragdoll]) {
                if (entity.bodygroupSignature) {
                    entity.bodygroupInput = null; entity.bodygroupSignature = null;
                    entity.bodygroupPath = null; entity.bodygroupManaged = []; entity.renderBody = undefined;
                    this.changed(entity);
                }
                continue;
            }
            const request = { path, body: appearance.body, equipment: player ? owners.get(entity.key) ?? [] : [],
                managed: entity.bodygroupPath === path ? entity.bodygroupManaged ?? [] : [],
                self: player ? undefined : { item: effects.item, team: effects.team, style: effects.itemStyle ?? UnknownStyle } };
            // Managed indices are output bookkeeping, not a reason to rerun.
            const signature = JSON.stringify({ ...request, managed: undefined });
            if (entity.bodygroupSignature === signature) continue;
            entity.bodygroupSignature = signature;
            if (entity.bodygroupPath !== path || entity.bodygroupBase !== appearance.body) entity.renderBody = undefined;
            const input = entity.bodygroupInput = {};
            Promise.resolve().then(() => this.rpc('bodygroups', request)).then(result => {
                if (revision !== this.revision || entities.get(entity.key) !== entity || entity.bodygroupInput !== input) return;
                if (!Number.isInteger(result.body) || result.body < 0 || result.body > 0xffffffff || !Array.isArray(result.managed)) throw new Error('Invalid bodygroup result');
                entity.bodygroupPath = path; entity.bodygroupBase = request.body; entity.bodygroupManaged = result.managed; entity.renderBody = result.body;
                this.changed(entity);
            }).catch(error => {
                if (revision === this.revision && entities.get(entity.key) === entity && entity.bodygroupInput === input)
                    this.diagnostic?.(`Bodygroups: ${error.message}`);
            });
        }
    }
}
